import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { StargraveCrewData, StargraveLeader } from '../../types';
import { stargravePowers } from '../../data/stargrave/coreRules';
import { PowerCard } from '../shared/PowerCard';

interface StargravePlayPowersTabProps {
  crewData: StargraveCrewData;
  minified?: boolean;
}

const inGamePowers = (leader: StargraveLeader) => leader.powers.flatMap((selection) => {
  const power = stargravePowers.find((candidate) => candidate.id === selection.powerId);
  if (!power || power.timing.startsWith('Out of Game')) return [];
  return [{ power, selection }];
});

export const StargravePlayPowersTab: React.FC<StargravePlayPowersTabProps> = ({ crewData, minified = false }) => {
  const leaders = [crewData.captain, crewData.firstMate].filter(
    (leader): leader is StargraveLeader => Boolean(leader)
  );

  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Box>
      <Typography variant="h5">Powers</Typography>
      <Typography color="text.secondary">
        Powers that can be cast during the game, grouped by owner.
      </Typography>
    </Box>

    {leaders.map((leader) => {
      const powers = inGamePowers(leader);
      return <Box key={leader.id}>
        <Typography variant="h6" sx={{ mb: 1 }}>{leader.name}</Typography>
        {powers.length === 0 ? (
          <Typography color="text.secondary">No in-game Powers.</Typography>
        ) : (
          <Box sx={minified
            ? { display: 'flex', flexWrap: 'wrap', gap: 2 }
            : { display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
            {powers.map(({ power, selection }) => <PowerCard
              key={power.id}
              name={power.name}
              activation={selection.activation}
              strain={selection.strain}
              timing={power.timing}
              summary={power.summary}
              minified={minified}
            />)}
          </Box>
        )}
      </Box>;
    })}

    {leaders.length === 0 && <Typography color="text.secondary">Create a Captain or First Mate to use Powers.</Typography>}
  </Box>;
};
